import { useEffect, useRef } from "react";
import { X, ArrowsClockwise, Desktop, DeviceMobile, SpinnerGap } from "phosphor-react";
import { Button } from "./ui/button";

/**
 * FullScreenPreviewModal - A full-screen modal for previewing emails
 * 
 * Props:
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {React.ReactNode} children - Preview content to render (EmailTemplatePreview)
 * @param {function} onRefresh - Callback for refresh button
 * @param {string} previewWidth - Current preview width mode ("desktop" | "mobile")
 * @param {function} onWidthChange - Callback when preview width changes
 * @param {boolean} loading - Whether preview is loading
 * @param {string} subject - Email subject for display
 */
export default function FullScreenPreviewModal({
  isOpen,
  onClose,
  children,
  onRefresh,
  previewWidth = "desktop",
  onWidthChange,
  loading = false,
  subject = "",
}) {
  const scrollPositionRef = useRef(0);

  // Save scroll position when opening, restore when closing
  useEffect(() => {
    if (isOpen) {
      scrollPositionRef.current = window.scrollY;
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      // Restore scroll position after a brief delay
      setTimeout(() => {
        window.scrollTo(0, scrollPositionRef.current);
      }, 10);
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-900/95 flex flex-col"
      data-testid="fullscreen-preview-modal"
    >
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 bg-zinc-800 border-b border-zinc-700 flex-shrink-0">
        <div className="flex items-center gap-4">
          <h2 className="font-heading text-lg font-semibold text-white uppercase tracking-tight">
            Email Preview
          </h2>
          {subject && (
            <span className="text-sm text-zinc-400 truncate max-w-md">
              {subject}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Preview Width Toggle */}
          <div className="flex items-center bg-zinc-700 rounded-lg p-1">
            <button
              onClick={() => onWidthChange?.("desktop")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                previewWidth === "desktop"
                  ? "bg-white text-zinc-900"
                  : "text-zinc-300 hover:text-white"
              }`}
              data-testid="preview-desktop-toggle"
            >
              <Desktop size={16} />
              Desktop
            </button>
            <button
              onClick={() => onWidthChange?.("mobile")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                previewWidth === "mobile"
                  ? "bg-white text-zinc-900"
                  : "text-zinc-300 hover:text-white"
              }`}
              data-testid="preview-mobile-toggle"
            >
              <DeviceMobile size={16} />
              Mobile
            </button>
          </div>

          {/* Refresh Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
            className="text-zinc-300 hover:text-white hover:bg-zinc-700"
            data-testid="preview-refresh-btn"
          >
            <ArrowsClockwise size={18} className={loading ? "animate-spin" : ""} />
          </Button>

          {/* Close Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-zinc-300 hover:text-white hover:bg-zinc-700"
            data-testid="close-preview-modal-btn"
          >
            <X size={20} />
            <span className="ml-2 text-sm">Close</span>
          </Button>
        </div>
      </header>

      {/* Preview Content - Scrollable, centered */}
      <div className="flex-1 overflow-y-auto p-6 flex justify-center">
        <div
          className="w-full transition-all duration-300"
          style={{
            maxWidth: previewWidth === "mobile" ? "400px" : "700px",
          }}
        >
          {loading ? (
            <div className="h-96 flex items-center justify-center">
              <SpinnerGap size={40} className="text-[#FF6600] animate-spin" />
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
              {children}
            </div>
          )}
        </div>
      </div>

      {/* Footer hint */}
      <div className="text-center py-3 text-xs text-zinc-500 flex-shrink-0">
        Press <kbd className="px-1.5 py-0.5 bg-zinc-700 rounded text-zinc-300">Esc</kbd> to close
      </div>
    </div>
  );
}
